<template>
  <div ref="cardRef" class="card-resumen">
    <div class="p-20">
      <div class="p text-landing semibold pb-3">
        {{ $t('reserva.eligeMetodoPago') }}
      </div>
      <div class="row g-2">
        <div v-for="pasarela in pasarelasList" :key="pasarela.id" class="col-6">
          <input
            :id="'pasarela-' + pasarela.id"
            v-model="selectedPasarelaId"
            :disabled="isPasarelaDisabled(pasarela)"
            type="radio"
            name="metodoPago"
            :value="pasarela.id"
            class="d-none"
            @change="onSelect(pasarela)" />

          <label
            :for="'pasarela-' + pasarela.id"
            class="metodo-item w-100 d-flex flex-column flex-md-row align-items-center justify-content-center pointer p-3 rounded position-relative"
            :class="{
              'metodo-item-selected': selectedPasarelaId === pasarela.id,
              'metodo-disabled': isPasarelaDisabled(pasarela),
            }"
            :data-bs-toggle="getMotivoPasarelaDisabled(pasarela) ? 'tooltip' : null"
            :data-bs-title="getMotivoPasarelaDisabled(pasarela)">
            <span class="metodo-item-icon position-absolute top-0 end-0">
              <RadioButtonIcon :is-selected="selectedPasarelaId === pasarela.id" />
            </span>
            <CustomIcon
              v-if="pasarela.icon"
              :icon="pasarela.icon"
              :alt="pasarela.integracion"
              class="metodo-item-icon-svg mb-2 mb-md-0 me-md-2 text-landing" />
            <span class="metodo-item-text small text-landing d-md-none text-center">{{ pasarela.tituloMobile }}</span>
            <span class="metodo-item-text p text-landing d-none d-md-inline text-center">{{ pasarela.titulo }}</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, onBeforeMount, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { Tooltip } from 'bootstrap';
  import RadioButtonIcon from '@/components/Icons/RadioButtonIcon.vue';
  import {
    CATEGORIAS_PASARELAS_CON_SALDO,
    CATEGORIAS_PASARELAS_SALDO,
    normalizePasarelasPago,
  } from '@/helpers/pagosUtils';
  import { formatPrice } from '@/helpers/numberUtils';
  import moment from 'moment-timezone';
  import UsuarioApiService from '@/services/api/UsuarioApiService';
  import { useUserStore } from '@/store_pinia/userStore';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import CustomIcon from '@/components/CustomIcon.vue';

  const props = defineProps({
    pasarelasPago: {
      type: Object,
      required: true,
    },
    alojamiento: {
      type: Object,
      default: () => null,
    },
    initialPasarelaPago: {
      type: Object,
      default: () => null,
    },
    importePagar: {
      type: Number,
      default: null,
    },
  });

  const emit = defineEmits(['metodo-pago-seleccionado']);

  const { t } = useI18n();
  const agenciaStore = agenciaStoreModule();
  const userStore = useUserStore();
  const loadingStore = useLoadingStore();

  const cardRef = ref(null);
  const selectedPasarelaId = ref(null);

  const pasarelasUsuario = ref([]);

  const pasarelasList = computed(() =>
    normalizePasarelasPago(props.pasarelasPago).filter(
      pasarela => !CATEGORIAS_PASARELAS_CON_SALDO.includes(pasarela.categoria) || getPasarelaUsuario(pasarela)
    )
  );

  // Selecciona la pasarela inicial y avisa al padre
  function selectPasarelaInicial() {
    const pasarelaInicial = resolvePasarelaInicial();
    if (pasarelaInicial) onSelect(pasarelaInicial);
  }

  // Devuelve la pasarela del usuario (con saldo y fecha de fin) que corresponde a la pasarela del viaje
  function getPasarelaUsuario(pasarela) {
    return pasarelasUsuario.value.find(pasarelaUsuario => pasarelaUsuario.id === pasarela.id);
  }

  function resolvePasarelaInicial() {
    const list = pasarelasList.value;
    if (!list?.length) return null;

    const initialId = props.initialPasarelaPago?.id;
    if (initialId != null) {
      const match = list.find(pasarela => Number(pasarela.id) === Number(initialId));
      if (match && !isPasarelaDisabled(match)) return match;
    }

    return list.find(pasarela => !isPasarelaDisabled(pasarela)) ?? list[0];
  }

  function onSelect(pasarela) {
    selectedPasarelaId.value = pasarela.id;
    emit('metodo-pago-seleccionado', pasarela);
  }

  function isPasarelaDisabled(pasarela) {
    return (
      !!getMotivoPasarelaDisabled(pasarela) ||
      (props.alojamiento !== null &&
        props.alojamiento.habitacion.integrado &&
        ((!props.alojamiento.habitacion.doesBlockBookingBeforePayment && !pasarela.refundEnabled) ||
          !pasarela.cuposIntegradosPermitidos))
    );
  }

  function getMotivoPasarelaDisabled(pasarela) {
    const pasarelaUsuario = getPasarelaUsuario(pasarela);
    if (!pasarelaUsuario) return null;
    if (pasarelaUsuario.fechaFin !== null && moment(pasarelaUsuario.fechaFin).isBefore(moment())) {
      return pasarela.categoria === CATEGORIAS_PASARELAS_SALDO.CREDITO
        ? t('reserva.creditoCaducado')
        : t('reserva.depositoCaducado');
    }
    if (
      props.importePagar !== null &&
      pasarelaUsuario.disponible !== null &&
      pasarelaUsuario.disponible < props.importePagar
    ) {
      const importe = `${formatPrice(pasarelaUsuario.disponible)} ${agenciaStore.getCurrency}`;
      return pasarela.categoria === CATEGORIAS_PASARELAS_SALDO.CREDITO
        ? t('reserva.creditoInsuficiente', { importe })
        : t('reserva.saldoInsuficiente', { importe });
    }
    return null;
  }

  function syncTooltips() {
    cardRef.value.querySelectorAll('.metodo-item').forEach(el => {
      Tooltip.getInstance(el)?.dispose();
      if (el.dataset.bsToggle === 'tooltip') {
        new Tooltip(el, { trigger: 'hover', container: el.closest('.landing-experiencia, .modal-mask') || false });
      }
    });
  }

  async function init() {
    try {
      loadingStore.show();
      if (userStore.isLogged && CATEGORIAS_PASARELAS_CON_SALDO.some(categoria => props.pasarelasPago?.[categoria])) {
        pasarelasUsuario.value = await UsuarioApiService.getPasarelasPago();
      }
    } catch (error) {
      console.error(error);
    } finally {
      loadingStore.hide();
      selectPasarelaInicial();
    }
  }

  // Cuando cambia el importe o se cargan las pasarelas del usuario: rehace los tooltips y, si la pasarela
  // seleccionada ha quedado deshabilitada, selecciona otra disponible
  watch(
    [() => props.importePagar, pasarelasUsuario],
    () => {
      syncTooltips();
      const selected = pasarelasList.value.find(pasarela => pasarela.id === selectedPasarelaId.value);
      if (!selected || !isPasarelaDisabled(selected)) return;
      const pasarela = resolvePasarelaInicial();
      if (pasarela) onSelect(pasarela);
    },
    { flush: 'post' }
  );

  onBeforeMount(init);
</script>

<style scoped lang="scss">
  .card-resumen {
    border-radius: var(--bs-border-radius-xl);
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.25);
  }

  .metodo-item {
    min-height: 56px;
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.25);
    transition: background-color 0.2s ease, border-color 0.2s ease;

    @media (max-width: 768px) {
      min-height: 72px;
    }
  }

  .metodo-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .metodo-item:hover:not(.metodo-disabled) {
    background-color: rgba(var(--landing-detail-color-rgb, var(--bs-primary-rgb)), 0.1);
  }

  .metodo-item-selected {
    background-color: rgba(var(--landing-detail-color-rgb, var(--bs-secondary-rgb)), 0.1);
    border-color: var(--landing-detail-color, var(--bs-secondary));
  }

  .metodo-item-icon {
    margin: 6px;
    transform: scale(0.8);
    transform-origin: top right;
  }

  .metodo-item-icon-svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  .metodo-item-text {
    text-align: center;
  }
</style>
